import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Rocket, Users, Building2, TrendingUp, CheckCircle2, Award } from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import PartieBadge from '../components/PartieBadge';
import { useLanguage } from '../contexts/LanguageContext';
import { phasesFR, phasesEN, planTexts } from '../planExecutionTranslations';

export default function PlanExecutionPage() {
  const { language } = useLanguage();
  const phases = language === 'fr' ? phasesFR : phasesEN;
  const t = planTexts[language];

  const icons = [Rocket, Users, TrendingUp, Building2, Award, Rocket];
  const colors = ['gray', 'lime', 'cyan', 'orange', 'purple', 'lime'];

  const colorClasses = {
    gray: { border: 'border-gray-500/30', bg: 'from-gray-500/15', text: 'text-gray-400' },
    lime: { border: 'border-lime-500/30', bg: 'from-lime-500/15', text: 'text-lime-400' },
    cyan: { border: 'border-cyan-500/30', bg: 'from-cyan-500/15', text: 'text-cyan-400' },
    orange: { border: 'border-orange-500/30', bg: 'from-orange-500/15', text: 'text-orange-400' },
    purple: { border: 'border-purple-500/30', bg: 'from-purple-500/15', text: 'text-purple-400' },
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        <PartieBadge path="/plan-execution" />
        <Card className="bg-black/60 border-orange-500/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl sm:text-3xl lg:text-4xl text-white mb-2">{t.titre}</CardTitle>
            <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-orange-500 to-lime-500 rounded-full" />
          </CardHeader>
          <CardContent className="space-y-8">
            <p className="text-gray-300 text-lg leading-relaxed">{t.intro}</p>

            {/* Bannière Pré-seed 6 mois */}
            <div className="p-5 bg-gradient-to-r from-orange-500/15 via-lime-500/10 to-transparent rounded-xl border-2 border-orange-500/40" data-testid="execution-preseed-banner">
              <div className="flex items-center gap-3 mb-2">
                <span className="px-3 py-1 bg-orange-500 text-black text-[10px] font-bold rounded-full uppercase tracking-wider">
                  {language === 'fr' ? 'Pré-seed 500K — Phase 1' : 'Pre-seed $500K — Phase 1'}
                </span>
                <span className="text-gray-300 text-sm">{language === 'fr' ? '6 mois : Septembre → Février' : '6 months: September → February'}</span>
              </div>
              <p className="text-gray-300 text-sm">
                {language === 'fr'
                  ? 'La période d\'exécution du pré-seed couvre 6 mois (Sept-Fév). À partir de février, EcoLunch prévoit lancer une ronde seed pour financer l\'expansion Phase 2 et au-delà.'
                  : 'The pre-seed execution period covers 6 months (Sep-Feb). Starting February, EcoLunch plans to launch a seed round to fund Phase 2 expansion and beyond.'}
              </p>
            </div>

            {phases.map((phase, idx) => {
              const Icon = icons[idx] || Rocket;
              const cc = colorClasses[colors[idx]] || colorClasses.lime;
              return (
                <Card key={idx} className={`bg-gradient-to-br ${cc.bg} to-transparent ${cc.border}`}>
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <Icon className={`h-8 w-8 ${cc.text}`} />
                      <div className="flex-1">
                        <h3 className={`text-xl font-bold ${cc.text}`}>{phase.titre}</h3>
                        <p className="text-sm text-gray-400">{phase.periode}</p>
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-black/40 rounded-lg">
                      <p className="text-sm text-gray-400">{t.objectifLabel}</p>
                      <p className="text-white font-semibold">{phase.objectif}</p>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-400 mb-3">{t.livrablesLabel}</p>
                      <div className="space-y-2">
                        {phase.delivrables.map((item, dIdx) => (
                          <div key={dIdx} className="flex items-start gap-2">
                            <CheckCircle2 className={`h-4 w-4 ${cc.text} flex-shrink-0 mt-0.5`} />
                            <span className="text-sm text-gray-300">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="pt-3 border-t border-gray-700">
                      <p className="text-xs text-gray-500 italic">{phase.justification}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            <NavigationControls />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
